import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import {
  useCreateDotInspectionMutation,
  useGetDotInspectionLookupsQuery,
} from "../../api/dotInspectionsApi";
import { BaseFormPage } from "../../components/common/BaseForm";
import { useFormSubmission } from "../../hooks/useFormSubmission";
import DotInspectionFormLayout from "./components/DotInspectionFormLayout";
import { PendingFile } from "./components/DotInspectionPendingFilesSection";
import {
  dotInspectionValidationSchema,
  getInitialDotInspectionFormValues,
  DotInspectionFormValues,
  parseOptionalNumber,
} from "./validation/dotInspectionFormValidation";
import { CreateDotInspectionDto } from "../../types/dotInspection";

const DotInspectionCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [createDotInspection] = useCreateDotInspectionMutation();
  const { data: lookups } = useGetDotInspectionLookupsQuery();
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);

  const {
    isSubmitting,
    submitError,
    successMessage,
    showValidationErrors,
    handleSubmit,
  } = useFormSubmission({
    successMessage: "DOT inspection created successfully!",
    onSuccess: () => {
      setTimeout(() => navigate("/dot-inspections"), 1500);
    },
  });

  const formik = useFormik<DotInspectionFormValues>({
    initialValues: getInitialDotInspectionFormValues(),
    validationSchema: dotInspectionValidationSchema,
    onSubmit: async (values) => {
      await handleSubmit(async () => {
        if (values.vehicleId == null) {
          throw new Error("Truck is required");
        }

        const dto: CreateDotInspectionDto = {
          inspectionDate: values.inspectionDate,
          claimCompanyId: values.claimCompanyId,
          claimCompanyName: values.claimCompanyName,
          driverEmployeeId: values.driverEmployeeId,
          vehicleId: values.vehicleId,
          trailerId: values.trailerId,
          points: parseOptionalNumber(values.points),
          description: values.description || null,
          amount: parseOptionalNumber(values.amount),
          driverCharged: values.driverCharged,
        };

        const newFiles = pendingFiles.filter((pf) => pf.file);
        const files = newFiles.map((pf) => pf.file!);
        const fileCategories = newFiles.map((pf) => pf.documentCategory || "");

        return await createDotInspection({ dto, files, fileCategories }).unwrap();
      });
    },
  });

  return (
    <BaseFormPage
      title="Create DOT Inspection"
      subtitle="Add a new DOT inspection"
      onBack={() => navigate("/dot-inspections")}
      formik={formik}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={submitError}
      showValidationErrors={showValidationErrors}
      submitButtonText="Create Inspection"
      maxWidth="lg"
    >
      <DotInspectionFormLayout
        availableCompanies={lookups?.availableCompanies || []}
        availableDrivers={lookups?.availableDrivers || []}
        availableTrucks={lookups?.availableTrucks || []}
        availableTrailers={lookups?.availableTrailers || []}
        pendingFiles={pendingFiles}
        onPendingFilesChange={setPendingFiles}
      />
    </BaseFormPage>
  );
};

export default DotInspectionCreatePage;
